import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsDownload, BsChevronDown, BsChevronRight } from 'react-icons/bs';

import Alert from '#globalComponents/Alert/Alert';
import Button from '#globalComponents/Button/Button';
import Checkbox from '#globalComponents/Checkbox/Checkbox';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';
import { useError } from '#globalContexts/ErrorContext';

const describeExportScope = (scope, count, { singular, plural }) => {
	const formattedCount = count.toLocaleString('de-DE');
	if (scope.sendIds) return `${formattedCount} ausgewählte ${count === 1 ? singular : plural}`;
	return `alle ${formattedCount} ${scope.filters ? 'gefilterten ' : ''}${plural}`;
};

const CampaignCSVExportModal = ({
	isOpen,
	onClose,
	campaignName,
	scope,
	count,
	columnGroups,
	defaultColumns,
	entityLabels,
	onExport,
	isExporting,
}) => {
	const { showSuccess } = useError();

	const [selectedColumns, setSelectedColumns] = useState(defaultColumns);
	const [expandedGroups, setExpandedGroups] = useState(() =>
		Object.fromEntries(Object.keys(columnGroups).map((groupKey) => [groupKey, true])),
	);

	const groupColumnKeys = (groupKey) => columnGroups[groupKey].columns.map((c) => c.key);

	const toggleGroup = (groupKey) => {
		setExpandedGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
	};

	const toggleColumn = (columnKey) => {
		setSelectedColumns((prev) =>
			prev.includes(columnKey) ? prev.filter((k) => k !== columnKey) : [...prev, columnKey],
		);
	};

	const selectAllInGroup = (groupKey) => {
		const groupCols = groupColumnKeys(groupKey);
		setSelectedColumns((prev) => [...new Set([...prev, ...groupCols])]);
	};

	const deselectAllInGroup = (groupKey) => {
		const groupCols = groupColumnKeys(groupKey);
		setSelectedColumns((prev) => prev.filter((k) => !groupCols.includes(k)));
	};

	const getSelectedCount = (groupKey) => groupColumnKeys(groupKey).filter((k) => selectedColumns.includes(k)).length;

	const isGroupFullySelected = (groupKey) => groupColumnKeys(groupKey).every((k) => selectedColumns.includes(k));

	const handleExport = async () => {
		try {
			await onExport(selectedColumns);
		} catch {
			// Error toast handled by the global MutationCache.
			return;
		}
		showSuccess({
			title: 'CSV-Export erfolgreich',
			message: 'Die Kampagnen-Daten wurden erfolgreich exportiert.',
		});
		onClose();
	};

	const totalColumns = Object.values(columnGroups).reduce((acc, group) => acc + group.columns.length, 0);

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="l">
			<Panel.Header onClose={onClose}>Kampagnen-Daten exportieren</Panel.Header>
			<Panel.Body>
				<div className="space-y-6">
					<div className="bg-bgBase-200 p-4 rounded-card">
						<p className="text-sm text-text">Export für Kampagne:</p>
						<p className="text-lg font-semibold text-text">{campaignName}</p>
						<p className="text-sm text-primary-600 mt-1 font-medium">
							Exportiert werden {describeExportScope(scope, count, entityLabels)}
						</p>
					</div>

					<div className="border-t border-bgBase-400 pt-4">
						<div className="flex items-center justify-between mb-3">
							<h3 className="text-md font-medium text-text">Spalten auswählen</h3>
							<span className="text-sm text-bgBase-800">
								{selectedColumns.length} von {totalColumns} ausgewählt
							</span>
						</div>

						<div className="space-y-3">
							{Object.entries(columnGroups).map(([groupKey, group]) => {
								const isExpanded = expandedGroups[groupKey];
								const selectedCount = getSelectedCount(groupKey);
								const totalCount = group.columns.length;

								return (
									<div
										key={groupKey}
										className="border border-bgBase-400 rounded-card overflow-hidden"
									>
										<div className="bg-bgBase-200 p-3">
											<div className="flex items-center justify-between">
												<button
													type="button"
													onClick={() => toggleGroup(groupKey)}
													className="flex items-center gap-2 text-sm font-medium text-text hover:text-text transition-colors"
												>
													{isExpanded ? (
														<BsChevronDown className="text-bgBase-800" />
													) : (
														<BsChevronRight className="text-bgBase-800" />
													)}
													{group.label}
													<span
														className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
															selectedCount > 0
																? 'bg-primary-200 text-primary-700'
																: 'bg-bgBase-400 text-text'
														}`}
													>
														{selectedCount}/{totalCount}
													</span>
												</button>
												<div className="flex gap-2">
													<Button
														variant="outline"
														size="sm"
														onClick={() => selectAllInGroup(groupKey)}
														disabled={isGroupFullySelected(groupKey)}
														className="text-xs"
													>
														Alle
													</Button>
													<Button
														variant="outline"
														size="sm"
														onClick={() => deselectAllInGroup(groupKey)}
														disabled={selectedCount === 0}
														className="text-xs"
													>
														Keine
													</Button>
												</div>
											</div>
										</div>

										{isExpanded && (
											<div className="p-3 bg-white">
												<div className="grid grid-cols-2 gap-2">
													{group.columns.map((column) => (
														<Checkbox
															key={column.key}
															id={`campaign-csv-column-${column.key}`}
															label={column.label}
															checked={selectedColumns.includes(column.key)}
															onChange={() => toggleColumn(column.key)}
														/>
													))}
												</div>
											</div>
										)}
									</div>
								);
							})}
						</div>

						{selectedColumns.length === 0 && (
							<Alert tone="caution" className="mt-3">
								Bitte wählen Sie mindestens eine Spalte aus.
							</Alert>
						)}
					</div>
				</div>
			</Panel.Body>
			<Panel.Footer className="justify-end">
				<Button variant="outline" onClick={onClose} disabled={isExporting}>
					Abbrechen
				</Button>
				<Button variant="solid" onClick={handleExport} disabled={isExporting || selectedColumns.length === 0}>
					{isExporting ? (
						<>
							<div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
							Exportiere...
						</>
					) : (
						<>
							<BsDownload />
							CSV Exportieren
						</>
					)}
				</Button>
			</Panel.Footer>
		</Modal>
	);
};

const columnGroupShape = PropTypes.shape({
	label: PropTypes.string.isRequired,
	columns: PropTypes.arrayOf(
		PropTypes.shape({ key: PropTypes.string.isRequired, label: PropTypes.string.isRequired }),
	).isRequired,
});

CampaignCSVExportModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	campaignName: PropTypes.string,
	/** Request scope: the selected sends, or every send matching the table filters. */
	scope: PropTypes.oneOfType([
		PropTypes.shape({ sendIds: PropTypes.arrayOf(PropTypes.string).isRequired }),
		PropTypes.shape({ selectAll: PropTypes.oneOf([true]).isRequired, filters: PropTypes.object }),
	]).isRequired,
	/** Sends covered by `scope`. */
	count: PropTypes.number.isRequired,
	/** Selectable columns per group; keys must match the backend export schema. */
	columnGroups: PropTypes.objectOf(columnGroupShape).isRequired,
	defaultColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
	entityLabels: PropTypes.shape({ singular: PropTypes.string.isRequired, plural: PropTypes.string.isRequired })
		.isRequired,
	/** Runs the export for the chosen column keys; rejects on failure (toast handled globally). */
	onExport: PropTypes.func.isRequired,
	isExporting: PropTypes.bool,
};

export default CampaignCSVExportModal;
